import { t, type I18nChildContext } from "@bookingcom/lingojs-react";
import type { CampaignImpactMetricName } from "../types";
import { CAMPAIGN_IMPACT_ABSOLUTE_METRIC_NAMES } from "../constants/campaignImpact";

const COUNT_METRICS: readonly CampaignImpactMetricName[] = CAMPAIGN_IMPACT_ABSOLUTE_METRIC_NAMES;

export const isPercentageMetric = (name: CampaignImpactMetricName): boolean => !COUNT_METRICS.includes(name);

/**
 * Round a KPI value to the same precision it is displayed with on the card.
 * Percentages are rounded to 1 decimal place; counts are rounded to an integer.
 * `-0` is normalized to `0` so downstream sign checks treat near-zero negatives as zero.
 */
export const roundKpiValue = (value: number, name: CampaignImpactMetricName): number => {
  const rounded = isPercentageMetric(name) ? Number(value.toFixed(1)) : Math.round(value);
  return rounded === 0 ? 0 : rounded;
};

/**
 * Absolute-number subtitle shown beneath the uplift percentage headlines
 * (e.g. "350 extra passengers" / "12 fewer bookings"). The copy switches between the
 * "extra" and "fewer" variants based on the sign of the rounded value; the text itself
 * is always rendered in green regardless of sign.
 *
 * Returns `undefined` for metrics that don't have an absolute-count subtitle.
 */
export const getUpliftSubtitle = (
  i18n: I18nChildContext,
  name: CampaignImpactMetricName,
  value: number
): string | undefined => {
  const rounded = roundKpiValue(value, name);
  const count = Math.abs(rounded);
  const isNegative = rounded < 0;

  switch (name) {
    case "UPLIFT_IN_PASSENGERS":
      return isNegative
        ? i18n.trans(
            t("flights_airline_portal_airline_view_campaign_metric_uplift_passengers_absolute_less_than_zero", {
              num_exception: count,
              variables: { num_passengers: count },
            })
          )
        : i18n.trans(
            t("flights_airline_portal_airline_view_campaign_metric_uplift_passengers_absolute_greater_than_zero", {
              num_exception: count,
              variables: { num_passengers: count },
            })
          );
    case "UPLIFT_IN_BOOKINGS":
      return isNegative
        ? i18n.trans(
            t("flights_airline_portal_airline_view_campaign_metric_uplift_bookings_absolute_less_than_zero", {
              num_exception: count,
              variables: { num_passengers: count },
            })
          )
        : i18n.trans(
            t("flights_airline_portal_airline_view_campaign_metric_uplift_bookings_absolute_greater_than_zero", {
              num_exception: count,
              variables: { num_passengers: count },
            })
          );
    default:
      return undefined;
  }
};
